import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { notificationsService } from "../services/notifications.service";

const PAGE_SIZE = 50;

export function useNotifications() {
  const queryClient = useQueryClient();

  const listQuery = useInfiniteQuery({
    queryKey: ["me", "notifications"],
    queryFn: ({ pageParam = 0 }) =>
      notificationsService.list({ limit: PAGE_SIZE, offset: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      // Backend returns a flat array with no total count, so infer "more
      // pages exist" from whether the last page was full. If it came back
      // short, we've hit the end.
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    refetchInterval: 30_000,
  });

  const countQuery = useQuery({
    queryKey: ["me", "notifications", "unread-count"],
    queryFn: notificationsService.unreadCount,
    refetchInterval: 30_000,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["me", "notifications"] });
    queryClient.invalidateQueries({ queryKey: ["me", "notifications", "unread-count"] });
  };

  const readMutation = useMutation({
    mutationFn: notificationsService.markRead,
    onSuccess: refresh,
  });

  const readAllMutation = useMutation({
    mutationFn: notificationsService.markAllRead,
    onSuccess: refresh,
  });

  const notifications = listQuery.data?.pages.flat() ?? [];

  return {
    notifications,
    unreadCount: countQuery.data?.unread_count ?? 0,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    markRead: readMutation.mutateAsync,
    markAllRead: readAllMutation.mutateAsync,
    isUpdating: readMutation.isPending || readAllMutation.isPending,
    fetchNextPage: listQuery.fetchNextPage,
    hasNextPage: listQuery.hasNextPage,
    isFetchingNextPage: listQuery.isFetchingNextPage,
  };
}